import { useCallback } from "react";

import {
  type DocumentData,
  type DocumentSnapshotModel,
  type ModelError,
  type ModelReference,
  type QuerySnapshotModel,
  type SnapshotListenOptions,
} from "@doitintl/models-any";
import { type QueryKey, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";

import { type QueryType, resolveQuery, type UseModelsHookOptions } from "./index";
import {
  useSubscriptionDocument,
  type UseSubscriptionDocumentOptions,
  useSubscriptionQuery,
  type UseSubscriptionQueryOptions,
} from "./useSubscriptionQuery";

type NextOrObserverQuery<TModel extends DocumentData> = (data: QuerySnapshotModel<TModel>) => void;

type NextOrObserverDocument<TModel extends DocumentData> = (data: DocumentSnapshotModel<TModel>) => void;

export const useModelsCollection = <TModel extends DocumentData, R = QuerySnapshotModel<TModel>>(
  queryKey: QueryKey | undefined,
  query: QueryType<TModel> | undefined,
  options?: UseModelsHookOptions,
  useQueryOptions?: Omit<UseQueryOptions<QuerySnapshotModel<TModel>, ModelError, R>, "queryFn">
): UseQueryResult<R, ModelError> => {
  const isSubscription = !!options?.subscribe;

  const subscribeFn = useCallback(
    async (callback: NextOrObserverQuery<TModel>, onError: (error: any) => void) => {
      const resolvedQuery = await resolveQuery<TModel>(query);

      if (resolvedQuery) {
        return resolvedQuery.onSnapshotWithOptions(options as SnapshotListenOptions | undefined, callback, onError);
      }

      return undefined;
    },
    [options, query]
  );

  const fetchFn = useCallback(
    async (): Promise<QuerySnapshotModel<TModel> | undefined> =>
      resolveQuery<TModel>(query).then((resolvedQuery) => resolvedQuery?.get(options)),
    [options, query]
  );

  return useSubscriptionQuery(queryKey, {
    ...useQueryOptions,
    enabled: Boolean(query) && Boolean(queryKey),
    subscribeFn: isSubscription ? subscribeFn : undefined,
    isSubscription,
    fetchFn: isSubscription ? undefined : fetchFn,
  } as UseSubscriptionQueryOptions<TModel, ModelError, R>);
};

export const useModelsDocument = <TModel extends DocumentData, R = DocumentSnapshotModel<TModel>>(
  queryKey: QueryKey | undefined,
  docRef: ModelReference<TModel> | undefined,
  options?: UseModelsHookOptions,
  useQueryOptions?: Omit<UseQueryOptions<ModelReference<TModel>, ModelError, R>, "queryFn">
): UseQueryResult<R, ModelError> => {
  const isSubscription = !!options?.subscribe;

  const subscribeDocFn = useCallback(
    (callback: NextOrObserverDocument<TModel>, onError: (error: any) => void) => {
      if (docRef) {
        return docRef.onSnapshotWithOptions(options as SnapshotListenOptions | undefined, callback, onError);
      }

      return undefined;
    },
    [options, docRef]
  );

  const fetchFn = useCallback(
    async (): Promise<DocumentSnapshotModel<TModel> | undefined> => docRef?.get(options),
    [options, docRef]
  );

  return useSubscriptionDocument(queryKey, {
    ...useQueryOptions,
    enabled: Boolean(docRef) && Boolean(queryKey),
    subscribeFn: isSubscription ? subscribeDocFn : undefined,
    isSubscription,
    fetchFn: isSubscription ? undefined : fetchFn,
  } as UseSubscriptionDocumentOptions<TModel, ModelError, R>);
};
